import { rawIfc } from '@viewer/lib/rawIfc'
import {
  TIPOS_ESSENCIAIS,
  type Unidade,
  lerUnidade,
  tipoDaMedida,
  unidadesDoProjecto,
} from '@viewer/lib/unidades'

/** As unidades de um modelo aberto, lidas pelo leitor em bruto
 *  (web-ifc). O motor de geometria perde as unidades por conversão (o
 *  grau) e a unidade própria de cada propriedade; o leitor em bruto tem
 *  tudo. Carrega-se a pedido, como já fazem o IDS e o BoQ. */

export interface UnidadesDoProjecto {
  porTipo: Map<string, Unidade>
  /** Tipos com mais de uma unidade declarada: ficam por determinar. */
  ambiguas: Map<string, Unidade[]>
  /** Dos tipos essenciais (comprimento, área, volume, ângulo), os que o
   *  projecto não declara. */
  emFalta: string[]
}

const cache = new Map<string, Promise<UnidadesDoProjecto | null>>()

async function garantirLeitor(modelId: string): Promise<boolean> {
  if (rawIfc.hasModel(modelId)) return true
  if (!rawIfc.hasCachedBytes(modelId)) return false
  try {
    await rawIfc.loadModel(modelId)
    return rawIfc.hasModel(modelId)
  } catch {
    return false
  }
}

/** As unidades que o projecto declara. Null se o modelo não veio de um
 *  IFC (um .frag não traz o ficheiro original). */
export function obterUnidadesDoProjecto(modelId: string): Promise<UnidadesDoProjecto | null> {
  let p = cache.get(modelId)
  if (!p) {
    p = (async () => {
      if (!(await garantirLeitor(modelId))) return null
      const projectos = rawIfc.getLineIDsByType(modelId, 'IFCPROJECT')
      if (projectos.length === 0) return null
      const projecto = rawIfc.getEntity(modelId, projectos[0]) as {
        UnitsInContext?: { Units?: unknown[] } | null
      } | null
      const units = projecto?.UnitsInContext?.Units ?? []
      const { porTipo, ambiguas } = unidadesDoProjecto(Array.isArray(units) ? units : [])
      return {
        porTipo,
        ambiguas,
        emFalta: TIPOS_ESSENCIAIS.filter((t) => !porTipo.has(t) && !ambiguas.has(t)),
      }
    })()
    cache.set(modelId, p)
    // Se falhar, não fica em cache: tenta-se outra vez na próxima.
    p.then((r) => {
      if (!r) cache.delete(modelId)
    }).catch(() => cache.delete(modelId))
  }
  return p
}

export function esquecerUnidades(modelId: string): void {
  cache.delete(modelId)
}

/** A unidade que uma propriedade ou quantidade declara, se declarar.
 *  Só responde com o leitor em bruto já carregado. */
export function unidadePropria(modelId: string, localId: number | undefined): Unidade | null {
  if (localId === undefined || !rawIfc.hasModel(modelId)) return null
  try {
    const e = rawIfc.getEntity(modelId, localId) as { Unit?: unknown } | null
    return e?.Unit ? lerUnidade(e.Unit) : null
  } catch {
    return null
  }
}

/** As classes de propriedade e de quantidade que podem trazer unidade
 *  própria, e o atributo onde o valor diz que medida é. */
const CLASSES_COM_UNIDADE: Array<[string, string]> = [
  ['IFCPROPERTYSINGLEVALUE', 'NominalValue'],
  ['IFCPROPERTYBOUNDEDVALUE', 'UpperBoundValue'],
  ['IFCPROPERTYLISTVALUE', 'ListValues'],
  ['IFCQUANTITYLENGTH', ''],
  ['IFCQUANTITYAREA', ''],
  ['IFCQUANTITYVOLUME', ''],
  ['IFCQUANTITYWEIGHT', ''],
  ['IFCQUANTITYTIME', ''],
]

export interface UnidadeUsada {
  unidade: Unidade
  n: number
  /** Alguns nomes de propriedades que a usam, para se poder ir ver. */
  exemplos: string[]
}

export interface AnaliseDasPropriedades {
  total: number
  /** Com valor numérico com dimensão (comprimento, área, força...). */
  comDimensao: number
  /** Das com dimensão, as que declaram a sua unidade. */
  comUnidadePropria: number
  /** As unidades declaradas nas propriedades, das mais usadas às menos. */
  porUnidade: UnidadeUsada[]
  /** Tipos de medida usados sem unidade própria nem do projecto. */
  semUnidade: Array<{ tipo: string; n: number; exemplos: string[] }>
}

function nomeDe(e: Record<string, unknown>): string {
  const n = e.Name as { value?: unknown } | null | undefined
  return n && typeof n === 'object' ? String(n.value ?? '') : String(n ?? '')
}

function medidaDe(e: Record<string, unknown>, atributo: string, classe: string): string {
  if (!atributo) return classe
  let v = e[atributo] as unknown
  if (Array.isArray(v)) v = v[0]
  if (v && typeof v === 'object') {
    const o = v as { name?: unknown; label?: unknown }
    return String(o.name ?? o.label ?? '')
  }
  return ''
}

const ceder = () => new Promise<void>((r) => setTimeout(r, 0))

/** Percorre as propriedades e quantidades do modelo e diz que unidades
 *  declaram. Pode demorar num modelo grande; cede ao browser de vez em
 *  quando. */
export async function analisarUnidadesDasPropriedades(
  modelId: string,
  aoProgredir: (feito: number, total: number) => void = () => {},
): Promise<AnaliseDasPropriedades | null> {
  const projecto = await obterUnidadesDoProjecto(modelId)
  if (!projecto) return null
  const listas = CLASSES_COM_UNIDADE.map(([c, a]) => [c, a, rawIfc.getLineIDsByType(modelId, c)] as const)
  const total = listas.reduce((s, [, , ids]) => s + ids.length, 0)
  const porSimbolo = new Map<string, UnidadeUsada>()
  const semUnidade = new Map<string, { tipo: string; n: number; exemplos: string[] }>()
  let feito = 0
  let comDimensao = 0
  let comUnidadePropria = 0
  for (const [classe, atributo, ids] of listas) {
    for (const id of ids) {
      feito += 1
      if (feito % 2000 === 0) {
        aoProgredir(feito, total)
        await ceder()
      }
      let e: Record<string, unknown> | null
      try {
        e = rawIfc.getEntity(modelId, id) as Record<string, unknown> | null
      } catch {
        continue
      }
      if (!e) continue
      const tipo = tipoDaMedida(medidaDe(e, atributo, classe))
      if (!tipo || tipo === 'ADIMENSIONAL') continue
      comDimensao += 1
      const nome = nomeDe(e)
      const u = e.Unit ? lerUnidade(e.Unit) : null
      if (u) {
        comUnidadePropria += 1
        const chave = `${u.tipo}|${u.simbolo}`
        const reg = porSimbolo.get(chave) ?? { unidade: u, n: 0, exemplos: [] }
        reg.n += 1
        if (reg.exemplos.length < 5 && nome && !reg.exemplos.includes(nome)) reg.exemplos.push(nome)
        porSimbolo.set(chave, reg)
      } else if (!projecto.porTipo.has(tipo) && !projecto.ambiguas.has(tipo)) {
        const reg = semUnidade.get(tipo) ?? { tipo, n: 0, exemplos: [] }
        reg.n += 1
        if (reg.exemplos.length < 5 && nome && !reg.exemplos.includes(nome)) reg.exemplos.push(nome)
        semUnidade.set(tipo, reg)
      }
    }
  }
  aoProgredir(total, total)
  return {
    total,
    comDimensao,
    comUnidadePropria,
    porUnidade: [...porSimbolo.values()].sort((a, b) => b.n - a.n),
    semUnidade: [...semUnidade.values()].sort((a, b) => b.n - a.n),
  }
}
